import { Fade } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { HeaderOpt } from './_type';
import useLeftNavContext from './left-nav-context';
import LeftNavHeader from './left-nav-header';

import Arranger from '~/client/components/base/arranger';
import { spc } from '~/client/components/base/spacer';

interface Props extends React.PropsWithChildren {
  className?: string;
  /**
   * Name of this route
   */
  name: string;
  /**
   * Options for rendering header
   */
  header?: HeaderOpt;
}

const LeftNavMenuImpl: React.FC<Props> = ({
  className,
  name,
  header,
  children,
}) => {
  const { state } = useLeftNavContext();
  const { navColumnOpen, currentRoute } = state;
  const show = currentRoute === name;

  return (
    <div
      className={className}
      style={{ pointerEvents: show ? 'auto' : 'none' }}
    >
      <Fade in={!!show} timeout={150} unmountOnExit>
        <Arranger className="v2-menu" direction="column">
          <LeftNavHeader
            className="v2-header"
            sx={{
              mb: spc.xxs,
              mt: navColumnOpen ? spc.xs : spc.xxxl,
            }}
            {...header}
          />
          <Arranger className="v2-menu-item-container" direction="column">
            {children}
          </Arranger>
        </Arranger>
      </Fade>
    </div>
  );
};

/**
 * Left Navigation Menu Container
 *
 * Container for placing a group of `<LeftNavMenuItem/>`
 */
export const LeftNavMenu = muiStyled(LeftNavMenuImpl)(
  ({ theme }) => css`
    /**
     * Each menu takes full width and height of container
     * And starts from the same absolute position, to make
     * animations between different sub menus possible
     */
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    .v2-menu {
      height: 100%;
      min-height: 0; /* Allow flex column to shrink so header stays visible */
      overflow: hidden;
      display: flex;
      flex-direction: column;

      .v2-header {
        z-index: 1;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        padding: 0 ${spc.container_padding_m}px;
        flex-shrink: 0; /* Keep logo and org switcher visible at top */
      }

      /**
       * Menu items take remaining space and scroll; header stays fixed above
       */
      .v2-menu-item-container {
        z-index: 1;
        flex: 1 1 0;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      }

      /* Prevent rows from shrinking when vertical space is limited; list scrolls instead */
      .v2-menu-item-container > * {
        flex-shrink: 0;
      }

      /* Submenu (children) icons: 20px - smaller than parents */
      .v2-menu-item-container .submenu-container .icon,
      .v2-menu-item-container .submenu-container [class*='MuiSvgIcon-root'] {
        font-size: 20px !important;
        width: 20px !important;
        height: 20px !important;
      }
    }
  `
);
